<script setup lang="ts">
import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import RcSiteSelect from '@/components/rc/common/RcSiteSelect.vue'
import { getForemanCurtailmentPlans } from '@/services/core/foreman'
import { getHostingClients } from '@/services/eos/site-map'
import type { IForemanCurtailmentPlan } from '@/types/core/ForemanCurtailmentPlan'
import { SiteType } from '@/types/core/Site'
import type { ISiteCurtailmentConfiguration } from '@/types/eos/SiteCurtailmentConfiguration'
import type { IHostingClient } from '@/types/eos/SiteMap'
import type { ISiteCurtailmentConfigurationUpsertForm } from '@/types/eos/forms'
import { Delete } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { reactive, ref } from 'vue'

const emit = defineEmits(['closed', 'create', 'update'])

const props = defineProps<{ configToUpdate?: ISiteCurtailmentConfiguration }>()

const hostingClientOptions = ref<OptionType[]>([])
const hostingClientOptionsLoading = ref(false)
const hostingClientIDToObjectMap = ref<Record<string, IHostingClient>>({})

const foremanCurtailmentPlanOptions = ref<OptionType[]>([])
const foremanCurtailmentPlanOptionsLoading = ref(false)

const selectedSiteRef = ref<ISiteIdentifier>()

const formRef = ref<FormInstance>()
const form = reactive<ISiteCurtailmentConfigurationUpsertForm>({
  site_id: undefined,
  client_configurations: [],
})

const formRules = reactive<FormRules<ISiteCurtailmentConfigurationUpsertForm>>({
  site_id: [{ required: true, message: 'Site is required.', trigger: ['change'] }],
  client_configurations: [{ required: true, type: 'array', min: 1, message: 'Client configurations are required.' }],
})

defineExpose({
  init: () => {
    const { configToUpdate } = props
    if (configToUpdate) {
      form.site_id = configToUpdate.site.site_id
      form.client_configurations = configToUpdate.client_configurations.map((cc) => ({
        key: cc.id,
        hosting_client_id: cc.hosting_client.id,
        ignore_uptime_threshold: cc.ignore_uptime_threshold,
        curtailment_plans: cc.curtailment_plans.map((cp) => ({
          key: cp.foreman_curtailment_plan_id,
          plan_id: cp.foreman_curtailment_plan_id,
          name: cp.foreman_curtailment_plan_name,
        })),
      }))

      selectedSiteRef.value = {
        site_id: configToUpdate.site.site_id,
        site_name: configToUpdate.site.name,
      }

      hostingClientOptions.value = configToUpdate.client_configurations.map((cc) => ({
        label: cc.hosting_client.name,
        value: cc.hosting_client.id,
      }))

      foremanCurtailmentPlanOptions.value = configToUpdate.client_configurations.flatMap((cc) =>
        cc.curtailment_plans.map((cp) => ({
          label: cp.foreman_curtailment_plan_name,
          value: cp.foreman_curtailment_plan_id,
        }))
      )

      configToUpdate.client_configurations.forEach((cc) => {
        hostingClientIDToObjectMap.value[cc.hosting_client.id] = cc.hosting_client
      })

      updateAvailableHostingClientOptions()
    } else {
      addClientConfig()
    }
    formRef.value?.clearValidate()
  },
  submit: () => {
    formRef.value?.validate((valid: boolean) => {
      if (!valid) {
        return
      }

      if (props.configToUpdate) {
        emit('update', props.configToUpdate.id, form)
      } else {
        emit('create', form)
      }
    })
  },
  resetFields: () => {
    formRef.value?.resetFields()
    form.client_configurations = []
    selectedSiteRef.value = undefined
    hostingClientOptions.value = []
    foremanCurtailmentPlanOptions.value = []
  },
})

const onSiteChange = (val: ISiteIdentifier) => {
  if (val) {
    selectedSiteRef.value = val
    form.site_id = val.site_id
    return
  }

  form.site_id = undefined
  selectedSiteRef.value = undefined
}

const removeClientConfig = (item: any) => {
  const index = form.client_configurations.indexOf(item)
  if (index !== -1) {
    form.client_configurations.splice(index, 1)
  }
}

const addClientConfig = () => {
  if (!form.client_configurations) {
    form.client_configurations = []
  }
  form.client_configurations.push({
    key: Date.now(),
    hosting_client_id: undefined,
    ignore_uptime_threshold: false,
    curtailment_plans: [],
  })
}

const handleHostingClientChange = (cfg: any) => {
  cfg.curtailment_plans = []
  updateAvailableHostingClientOptions()
}

const updateAvailableHostingClientOptions = () => {
  const selectedOptions = form.client_configurations?.map((cfg) => cfg.hosting_client_id) ?? []
  hostingClientOptions.value.forEach((option) => (option.disabled = selectedOptions.includes(option.value)))
}

const handleHostingClientSearch = async (query: string) => {
  const selectedHostingClientIDs = form.client_configurations?.map((cfg) => cfg.hosting_client_id) ?? []
  try {
    hostingClientOptionsLoading.value = true
    const hostingClients = await getHostingClients({
      page: 1,
      per_page: 10,
      order_by: 'name',
      name: query,
    })
    hostingClientOptions.value =
      hostingClients.data?.map((hc: IHostingClient) => ({
        label: hc.name,
        value: hc.id,
        disabled: selectedHostingClientIDs.includes(hc.id),
      })) ?? []
    hostingClientIDToObjectMap.value =
      hostingClients.data?.reduce(
        (acc, hc) => {
          acc[hc.id] = hc
          return acc
        },
        {} as Record<string, IHostingClient>
      ) ?? {}
  } catch {
    ElMessage.error('Failed to fetch hosting clients')
    hostingClientOptions.value = []
  } finally {
    hostingClientOptionsLoading.value = false
  }
}

const handleCurtailmentPlansSearch = async (query: string, hostingClient: IHostingClient | undefined) => {
  if (!hostingClient || !hostingClient.foreman_id) {
    foremanCurtailmentPlanOptions.value = []
    return
  }

  try {
    foremanCurtailmentPlanOptionsLoading.value = true
    const plans = await getForemanCurtailmentPlans({
      foreman_client_id: hostingClient.foreman_id,
    })
    foremanCurtailmentPlanOptions.value = plans
      .filter((plan: IForemanCurtailmentPlan) => plan.name.toLowerCase().includes(query.toLowerCase()))
      .map((plan: IForemanCurtailmentPlan) => ({
        label: plan.name,
        value: plan,
      }))
  } catch {
    ElMessage.error('Failed to fetch curtailment plans')
    foremanCurtailmentPlanOptions.value = []
  } finally {
    foremanCurtailmentPlanOptionsLoading.value = false
  }
}

const handleHostingClientCurtailmentPlanChange = (plans: IForemanCurtailmentPlan[], cfg: any) => {
  cfg.curtailment_plans = plans.map((plan) => ({
    key: plan.plan_id,
    plan_id: plan.plan_id,
    name: plan.name,
  }))
}
</script>

<template>
  <el-form
    ref="formRef"
    :model="form"
    :rules="formRules"
    :hide-required-asterisk="true"
    label-position="left"
    :label-width="160"
  >
    <el-form-item label="Site" prop="site_id">
      <RcSiteSelect v-model="selectedSiteRef" :type="SiteType.DataCenter" placeholder="Site" @change="onSiteChange" />
    </el-form-item>
    <el-form-item label="Client Configuration" prop="client_configurations" required>
      <div class="w-full flex flex-col gap-4">
        <el-form-item v-for="(cfg, index) in form.client_configurations" :key="cfg.key">
          <div class="w-full inline-flex gap-4">
            <el-form-item
              :key="`${cfg.key}.hosting_client`"
              :label="index === 0 ? 'Hosting Client' : ''"
              label-position="top"
              :prop="`client_configurations.${index}.hosting_client_id`"
              :rules="{
                required: true,
                message: 'Hosting Client required.',
                trigger: 'change',
              }"
            >
              <el-select-v2
                v-model="cfg.hosting_client_id"
                placeholder="Hosting Client"
                :options="hostingClientOptions"
                remote
                :remote-method="(query: string) => handleHostingClientSearch(query)"
                :loading="hostingClientOptionsLoading"
                filterable
                clearable
                value-key="id"
                no-data-text="No hosting clients found"
                @change="(_: IHostingClient) => handleHostingClientChange(cfg)"
                @clear="() => handleHostingClientChange(cfg)"
              />
            </el-form-item>
            <el-form-item
              :key="`${cfg.key}.hosting_client_curtailment_plans`"
              :label="index === 0 ? 'Curtailment Plans' : ''"
              label-position="top"
              class="w-280"
              :prop="`client_configurations.${index}.curtailment_plans`"
              :rules="{
                required: true,
                message: 'Plans required.',
                trigger: 'change',
              }"
            >
              <div class="w-full flex flex-row gap-4">
                <el-select-v2
                  v-model="cfg.curtailment_plans"
                  placeholder="Plans"
                  :options="foremanCurtailmentPlanOptions"
                  :disabled="!cfg.hosting_client_id"
                  remote
                  multiple
                  :remote-method="
                    (query: string) =>
                      handleCurtailmentPlansSearch(query, hostingClientIDToObjectMap[cfg.hosting_client_id!])
                  "
                  :loading="foremanCurtailmentPlanOptionsLoading"
                  :loading-text="`Loading...`"
                  no-data-text="No plans found for this hosting client"
                  value-key="plan_id"
                  filterable
                  clearable
                  collapse-tags
                  collapse-tags-tooltip
                  :max-collapse-tags="1"
                  @change="(plans: IForemanCurtailmentPlan[]) => handleHostingClientCurtailmentPlanChange(plans, cfg)"
                >
                  <template #loading>
                    <div class="w-full inline-flex items-center justify-center">
                      <Icon icon="eos-icons:three-dots-loading" height="20" />
                    </div>
                  </template>
                </el-select-v2>
              </div>
            </el-form-item>

            <!-- Configuration Flags Section -->
            <el-form-item
              :key="`${cfg.key}.configuration_flags`"
              :label="index === 0 ? 'Configuration Flags' : ''"
              label-position="top"
              class="w-64 flex-shrink-0"
            >
              <div class="w-full flex flex-col gap-3">
                <!-- Ignore Uptime Threshold Flag -->
                <div class="flex items-center justify-between">
                  <span class="text-sm text-gray-600 dark:text-gray-400">Ignore Uptime Threshold</span>
                  <el-switch v-model="cfg.ignore_uptime_threshold" inline-prompt />
                </div>
              </div>
            </el-form-item>
            <div
              v-if="form.client_configurations.length >= 1"
              :class="`flex items-center justify-center ${index === 0 ? 'mt-7' : ''}`"
            >
              <el-button link :icon="Delete" @click="removeClientConfig(cfg)" />
            </div>
          </div>
        </el-form-item>
        <el-button class="self-start" link type="primary" @click="addClientConfig"
          >+ Add client configuration</el-button
        >
      </div>
    </el-form-item>
  </el-form>
</template>
